import { useRef, useState } from "react";
import { Props } from "./types";
import classNames from "classnames";
import DataContainer from "../../services/DataContainer";
import { ContactDealerFormSettings } from "../../enum/enums";
import { generateAccessibleId } from "../../utils/accessibility";

export const Input = ({
  type,
  variant,
  value,
  isValid = true,
  isRequired,
  onChangeEvent,
  name,
  label,
  checked,
  onKeyDownEvent,
  onClickEvent,
  customClassNames,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
  placeholder,
  autoComplete,
  maxLength,
  minLength,
  pattern,
  errorMessage,
}: Props) => {

  const [toggleClassLabel, setToggleClassLabel] = useState(false);
  const getInputs = useRef(null);
  const getLabels = useRef(null);
  const returnClass = (isActive: boolean) => {
    Object.entries(getInputs).forEach((input: HTMLInputElement[]) => {
      if (input[1] !== null) {
        if (input[1].value !== "") {
          Object.entries(getLabels).forEach((label: HTMLLabelElement[]) => {
            if (label[1] !== null) {
              if (label[1].htmlFor === input[1].name) {
                label[1].classList.remove("-translate-y-spacing-h-xl");
                label[1].classList.add("-translate-y-spacing-h-lg", "uppercase", "text-blue", "text-xl");
              }
            }
          });
        }
      }
    });
    if (isActive || type === "tel" ) {
      return "uppercase text-blue text-xl -translate-y-spacing-h-lg";
    }
    return "-translate-y-spacing-h-xl";
  };

  // Generate unique IDs for accessibility
  const inputId = generateAccessibleId("input", name);
  const errorId = generateAccessibleId("error", name);
  const labelId = generateAccessibleId("label", name);

  // Determine if input has error state
  const hasError = !isValid || (isRequired && value === "");
  const finalAriaInvalid = ariaInvalid !== undefined ? ariaInvalid : hasError;
  const finalAriaRequired = ariaRequired !== undefined ? ariaRequired : isRequired;

  // Generate error message
  const getErrorMessage = () => {
    if (errorMessage) return errorMessage;
    if (isRequired && value === "") {
      return DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, `contact_dealer_${name}_required_text`);
    }
    return "";
  };

  const errorMsg = getErrorMessage();

  return (
    <div
      className={classNames(
        {
          "bg-primary": variant === "primary",
          "bg-white": variant === "secondary",
          "bg-transparent": variant === "mailForm",
        },
        "relative justify-end cursor-pointer",
        { "flex flex-row-reverse flex-reverse items-center text-lg text-light-grey flex-start": type === "radio" },
        { "w-full h-full flex flex-col": type !== "checkbox" && type !== "radio" },
        { "flex flex-row-reverse items-center": type === "checkbox" },
      )}
    >
      <label
        ref={getLabels}
        id={labelId}
        className={classNames(
          customClassNames,
          variant !== "default" && returnClass(toggleClassLabel),
          { "absolute transition ease-in-out": variant !== "default" },
          { "font-normal items-center normal-case text-2xs": type === "checkbox" },
          {"flex" : type === "radio"}
        )}
        htmlFor={inputId}
      >
        {label}
        {isRequired && <span className="text-red ml-1" aria-label="required">*</span>}
      </label>
      <input
        ref={getInputs}
        id={inputId}
        type={type}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy || (hasError ? errorId : undefined)}
        aria-invalid={finalAriaInvalid}
        aria-required={finalAriaRequired}
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={maxLength}
        minLength={minLength}
        pattern={pattern}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        className={classNames(
          customClassNames,
          {
            "h-16 bg-transparent pt-5 leading-6 pb-4 pl-2 focus:outline-none border-b border-black":
              variant !== "default",
          },
          { "border border-light-grey px-2 py-2": variant === "default" },
          { "mb-4": isValid },
          { " mr-1": type === "checkbox" },
          {"border rounded-full border-blue-400 mb-0 mr-2 px-1.5 py-1.5 scale-100": type === "radio"},
          {"border-red focus:ring-red": hasError}
        )}
        value={type === "radio" ? label : value}
        onChange={onChangeEvent}
        onClick={onClickEvent}
        name={name}
        checked={checked}
        onKeyDown={onKeyDownEvent}
        required={isRequired}
        onFocus={() => {
          variant !== "default" && setToggleClassLabel(true);
        }}
        onBlur={() => {
          if (variant !== "default" && value === "") setToggleClassLabel(false);
        }}
      />
      {hasError && errorMsg && (
        <span 
          id={errorId}
          className="text-red text-xl h-4"
          role="alert"
          aria-live="polite"
        >
          {errorMsg}
        </span>
      )}
    </div>
  );
};
